/**
 * Hook para gerenciar operações offline
 * Detecta se está online e, se offline, salva operações na fila para sincronização posterior
 */

import { useCallback } from 'react';
import { useNetworkStatus, checkNetworkStatus } from './useNetworkStatus';
import {
  addToOfflineQueue,
  OfflineOperationType,
  handleOfflineMutationOfPendingRecord,
} from '@/lib/offlineStorage';
import {
  cacheQuotes,
  getCachedQuotes,
  cacheClients,
  getCachedClients,
  cacheCatalog,
  stripCachedCatalogMedia,
  getCachedCatalog,
  cacheFinanceEntries,
  getCachedFinanceEntries,
  cacheServiceOrders,
  getCachedServiceOrders,
  cacheAppointments,
  getCachedAppointments,
  cacheContracts,
  getCachedContracts,
  cacheReceipts,
  getCachedReceipts,
  cacheStockMovements,
  getCachedStockMovements,
} from '@/lib/offlineDataCache';
import { computeFinanceStatus } from '@/lib/financeStatus';
import { toast } from 'sonner';

export interface OfflineQuoteData {
  quote: any;
  items: any[];
  userId: string;
  companyId: string | null;
  /**
   * Ausente/true = orçamento normal, consome cota ao sincronizar.
   * false = Venda Rápida (pedido já consumado), nunca consome cota.
   */
  countTowardQuoteQuota?: boolean;
}

/** Options shared by the offline save helpers. */
export interface OfflineSaveOptions {
  /**
   * Suppress the "salvo localmente" toast. Used when the save is a side effect
   * of another save (e.g. a client/item created together with a quote) so the
   * user sees a single confirmation instead of a stack of toasts.
   */
  silent?: boolean;
}

/**
 * Replace base64 `data:` image URLs in a cached offline quote's nested items and
 * photos with null. The full images stay in the offline queue op (used for
 * sync), so nothing is lost — this only trims the list-cache copy so a quote
 * with heavy photos still fits in localStorage and stays visible in the lists.
 */
function stripCachedQuoteMedia(quote: any): any {
  if (!quote || quote._offline_pending !== true) return quote;
  const isDataUrl = (v: any) => typeof v === 'string' && v.startsWith('data:');
  const items = Array.isArray(quote._offline_items)
    ? quote._offline_items.map((it: any) =>
        isDataUrl(it?.image_url) ? { ...it, image_url: null } : it,
      )
    : quote._offline_items;
  const photos = Array.isArray(quote._offline_photos)
    ? quote._offline_photos.map((p: any) =>
        isDataUrl(p?.imageUrl) ? { ...p, imageUrl: null } : p,
      )
    : quote._offline_photos;
  return { ...quote, _offline_items: items, _offline_photos: photos };
}

/**
 * Strip base64 `data:` image URLs from a cached offline service order's nested
 * items and photos (mirrors stripCachedQuoteMedia). The full images stay in the
 * queue op for sync; this only trims the list-cache copy so a heavy SO still
 * fits in localStorage and stays visible in the lists.
 */
function stripCachedServiceOrderMedia(order: any): any {
  if (!order || order._offline_pending !== true) return order;
  const isDataUrl = (v: any) => typeof v === 'string' && v.startsWith('data:');
  const items = Array.isArray(order._offline_items)
    ? order._offline_items.map((it: any) =>
        isDataUrl(it?.image_url) ? { ...it, image_url: null } : it,
      )
    : order._offline_items;
  const photos = Array.isArray(order._offline_photos)
    ? order._offline_photos.map((p: any) =>
        isDataUrl(p?.imageUrl) || isDataUrl(p?.image_url)
          ? { ...p, imageUrl: null, image_url: null }
          : p,
      )
    : order._offline_photos;
  return { ...order, _offline_items: items, _offline_photos: photos };
}

/**
 * Strip heavy base64 `data:` signature URLs from a cached offline contract. The
 * DRAFT rarely carries a signature (signing is online), but a provider may have
 * signed an already-cached row online — so a re-cache under quota pressure nulls
 * the big data URLs while keeping the row (title/content/status) visible.
 */
function stripCachedContractMedia(contract: any): any {
  if (!contract) return contract;
  const isDataUrl = (v: any) => typeof v === 'string' && v.startsWith('data:');
  return {
    ...contract,
    provider_signature: isDataUrl(contract.provider_signature) ? null : contract.provider_signature,
    client_signature: isDataUrl(contract.client_signature) ? null : contract.client_signature,
  };
}

/**
 * Strip the heavy base64 `data:` issuer-signature URL from a cached offline
 * receipt (the receipt's only large field). The full data stays in the queue op
 * for sync; this only trims the list-cache copy under quota pressure so the row
 * (payer/amount/description) stays visible in the list.
 */
function stripCachedReceiptMedia(receipt: any): any {
  if (!receipt) return receipt;
  const isDataUrl = (v: any) => typeof v === 'string' && v.startsWith('data:');
  return {
    ...receipt,
    issuer_signature: isDataUrl(receipt.issuer_signature) ? null : receipt.issuer_signature,
  };
}

export interface OfflineServiceOrderData {
  serviceOrder: any;
  items: any[];
  photos?: any[];
  userId: string;
  companyId: string | null;
}

export interface OfflineClientData {
  client: {
    name: string;
    phone: string | null;
    address: string | null;
    document?: string | null;
    email?: string | null;
    cep?: string | null;
    logradouro?: string | null;
    numero?: string | null;
    complemento?: string | null;
    bairro?: string | null;
    municipio_nome?: string | null;
    municipio_ibge?: string | null;
    uf?: string | null;
    inscricao_estadual?: string | null;
  };
  userId: string;
  companyId: string | null;
}

export interface OfflineCatalogItemData {
  item: {
    name: string;
    description: string | null;
    price: number;
    /**
     * Custo (compra/produção). `null` = não informado — NUNCA 0, que
     * significaria "de graça" e inflaria margem (armadilha #61). Declarado
     * explicitamente porque o executor `create_catalog_item` monta o produto
     * por LISTA de colunas: campo ausente aqui vira custo em branco no banco,
     * com toast de sucesso e sem erro nenhum (armadilha #33/#62).
     */
    cost?: number | null;
    type: string;
    unit: string | null;
    image_url: string | null;
    barcode?: string | null;
    /** Código interno/SKU do prestador — distinto do `barcode` do fabricante. */
    internal_code?: string | null;
    category?: string | null;
    lc116_item?: string | null;
    iss_aliquota?: number | null;
    nbs?: string | null;
    cclasstrib?: string | null;
    track_stock?: boolean;
    min_stock?: number | null;
    /** null = herda automação da empresa; ausência nunca equivale a `off`. */
    stock_deduction_mode?: 'off' | 'ask' | 'auto' | null;
    /** Até 3 fotos; offline ficam como dataURL nesta operação até o sync. */
    product_photos?: Array<{
      image_url: string;
      title?: string | null;
      description?: string | null;
      position: number;
    }>;
    /**
     * Variações: só chegam aqui preenchidos por um item já REAL (o app bloqueia
     * criar/gerenciar variações offline — a fila mapeia 1 operação → 1 uuid
     * (registerOfflineIdMapping), então não haveria como resolver o pai de uma
     * filha criada offline). Os campos existem para o payload ficar fiel; o
     * executor descarta um `parent_product_id` que ainda seja `offline_`.
     */
    parent_product_id?: string | null;
    variant_name?: string | null;
  };
  userId: string;
  companyId: string | null;
}

export function useOfflineSync() {
  const { isOnline } = useNetworkStatus();

  /**
   * Adiciona um orçamento à fila offline e ao cache local
   * Inclui items e photos no cache para exibição quando reabrir o orçamento offline
   */
  const saveQuoteOffline = useCallback((data: OfflineQuoteData & { generalPhotos?: any[] }, options?: OfflineSaveOptions): string => {
    const operationId = addToOfflineQueue('create_quote', data);
    
    // Adicionar ao cache local para exibição imediata na lista
    const cachedQuotes = getCachedQuotes(data.userId) || [];
    const tempQuote = {
      id: operationId, // ID temporário
      ...data.quote,
      user_id: data.userId,
      company_id: data.companyId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      _offline_pending: true, // Marcador para indicar que está pendente
      // Store items and photos in the cached quote for offline retrieval
      _offline_items: data.items || [],
      _offline_photos: data.generalPhotos || [],
    };
    cachedQuotes.unshift(tempQuote); // Adicionar no início

    // Persist. If the write is rejected (localStorage quota — typically caused
    // by base64 photos in _offline_items/_offline_photos), retry with the media
    // stripped from the pending quotes so the quote still shows up in the lists.
    // The full images remain in the queue op above and sync normally.
    if (!cacheQuotes(data.userId, cachedQuotes)) {
      const slimmed = cachedQuotes.map((q: any) => stripCachedQuoteMedia(q));
      cacheQuotes(data.userId, slimmed);
    }

    // Dispatch event para atualizar listas
    window.dispatchEvent(new CustomEvent('offline-queue-updated'));
    window.dispatchEvent(new CustomEvent('quotes-updated'));

    // silent: o chamador dá a própria confirmação (ex.: fluxo de PDF offline
    // mostra um único toast combinado "salvo offline e PDF gerado").
    if (!options?.silent) {
      toast.success('Orçamento salvo localmente! Será sincronizado quando houver internet.', {
        icon: '📴',
      });
    }

    return operationId;
  }, []);

  /**
   * Adiciona um cliente à fila offline e ao cache local
   */
  const saveClientOffline = useCallback((data: OfflineClientData, options?: OfflineSaveOptions): string => {
    const operationId = addToOfflineQueue('create_client', data);

    // Adicionar ao cache local
    const cachedClients = getCachedClients(data.userId) || [];
    const tempClient = {
      id: operationId,
      ...data.client,
      user_id: data.userId,
      company_id: data.companyId,
      created_at: new Date().toISOString(),
      _offline_pending: true,
    };
    cachedClients.unshift(tempClient);
    cacheClients(data.userId, cachedClients);

    window.dispatchEvent(new CustomEvent('offline-queue-updated'));
    window.dispatchEvent(new CustomEvent('clients-updated'));

    if (!options?.silent) {
      toast.success('Cliente salvo localmente! Será sincronizado quando houver internet.', {
        icon: '📴',
      });
    }

    return operationId;
  }, []);

  /**
   * Adiciona um item do catálogo à fila offline e ao cache local
   */
  const saveCatalogItemOffline = useCallback((data: OfflineCatalogItemData, options?: OfflineSaveOptions): string => {
    const operationId = addToOfflineQueue('create_catalog_item', data);

    // Adicionar ao cache local
    const cachedCatalog = getCachedCatalog(data.userId) || [];
    const tempItem = {
      id: operationId,
      ...data.item,
      user_id: data.userId,
      company_id: data.companyId,
      created_at: new Date().toISOString(),
      _offline_pending: true,
      _offline_photos: data.item.product_photos ?? [],
    };
    cachedCatalog.unshift(tempItem);

    // Persist. If the write is rejected (localStorage quota — typically caused
    // by the base64 photos of an item cadastrado offline), retry with the media
    // stripped so the item still shows up in the catalog list and, above all, in
    // the CatalogSelector — usá-lo no orçamento que se estava montando é o
    // motivo de tê-lo cadastrado ali. As imagens cheias seguem na op da fila
    // acima e sincronizam normalmente. Mesmo retry de orçamento/OS/contrato/
    // recibo (defeito D9).
    if (!cacheCatalog(data.userId, cachedCatalog)) {
      cacheCatalog(data.userId, cachedCatalog.map(stripCachedCatalogMedia));
    }

    window.dispatchEvent(new CustomEvent('offline-queue-updated'));
    window.dispatchEvent(new CustomEvent('catalog-updated'));

    if (!options?.silent) {
      toast.success('Item salvo localmente! Será sincronizado quando houver internet.', {
        icon: '📴',
      });
    }

    return operationId;
  }, []);

  /**
   * Enfileira a criação de um lançamento financeiro e adiciona ao cache local
   * para exibição imediata. Retorna o id temporário (offline_...).
   */
  const saveFinanceEntryOffline = useCallback(
    (data: { entry: Record<string, any>; userId: string }): string => {
      const operationId = addToOfflineQueue('create_finance_entry', data);

      const cached = getCachedFinanceEntries(data.userId) || [];
      const now = new Date().toISOString();
      const tempEntry = {
        id: operationId,
        ...data.entry,
        user_id: data.userId,
        created_at: now,
        updated_at: now,
        _offline_pending: true,
        computed_status: computeFinanceStatus(data.entry.status, data.entry.due_date),
      };
      cached.unshift(tempEntry);
      cacheFinanceEntries(data.userId, cached);

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('finance-updated'));

      return operationId;
    },
    [],
  );

  /**
   * Enfileira a edição de um lançamento. Se o lançamento ainda é offline
   * (create pendente), dobra a edição no create para não enviar um id
   * offline_ ao Postgres (ver [[orcamigo-offline-sync-uuid-bug]]).
   */
  const updateFinanceEntryOffline = useCallback(
    (data: { entryId: string; updates: Record<string, any>; userId: string }): void => {
      const folded = String(data.entryId).startsWith('offline_')
        ? handleOfflineMutationOfPendingRecord(data.entryId, { updates: data.updates })
        : { removed: false };

      if (!folded.removed) {
        addToOfflineQueue('update_finance_entry', data);
      }

      const cached = getCachedFinanceEntries(data.userId) || [];
      const idx = cached.findIndex((e: any) => e.id === data.entryId);
      if (idx !== -1) {
        const merged = { ...cached[idx], ...data.updates, _offline_pending: true };
        merged.computed_status = computeFinanceStatus(merged.status, merged.due_date);
        cached[idx] = merged;
        cacheFinanceEntries(data.userId, cached);
      }

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('finance-updated'));
    },
    [],
  );

  /**
   * Enfileira a exclusão de um lançamento. Se ainda é offline, apenas remove o
   * create pendente da fila.
   */
  const deleteFinanceEntryOffline = useCallback(
    (data: { entryId: string; userId: string }): void => {
      const handled = String(data.entryId).startsWith('offline_')
        ? handleOfflineMutationOfPendingRecord(data.entryId)
        : { removed: false };

      if (!handled.removed) {
        addToOfflineQueue('delete_finance_entry', data);
      }

      const cached = getCachedFinanceEntries(data.userId) || [];
      cacheFinanceEntries(data.userId, cached.filter((e: any) => e.id !== data.entryId));

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('finance-updated'));
    },
    [],
  );

  /**
   * Enfileira a criação de uma ordem de serviço e adiciona ao cache local para
   * exibição imediata. Itens e fotos ficam embutidos no cache (_offline_items /
   * _offline_photos) para renderizar offline, como no fluxo de orçamento.
   * Retorna o id temporário (offline_...).
   */
  const saveServiceOrderOffline = useCallback(
    (data: OfflineServiceOrderData, options?: OfflineSaveOptions): string => {
      const operationId = addToOfflineQueue('create_service_order', data);

      const cached = getCachedServiceOrders(data.userId) || [];
      const now = new Date().toISOString();
      const tempOrder = {
        id: operationId,
        ...data.serviceOrder,
        user_id: data.userId,
        company_id: data.companyId,
        created_at: now,
        updated_at: now,
        _offline_pending: true,
        _offline_items: data.items || [],
        _offline_photos: data.photos || [],
      };
      cached.unshift(tempOrder);

      // Retry with media stripped if the write is rejected (quota — typically
      // base64 photos). The full images remain in the queue op and sync normally.
      if (!cacheServiceOrders(data.userId, cached)) {
        const slimmed = cached.map((o: any) => stripCachedServiceOrderMedia(o));
        cacheServiceOrders(data.userId, slimmed);
      }

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('service-order-updated'));

      if (!options?.silent) {
        toast.success('Ordem de serviço salva localmente! Será sincronizada quando houver internet.', {
          icon: '📴',
        });
      }

      return operationId;
    },
    [],
  );

  /**
   * Enfileira a edição de uma OS. Se ainda é offline (create pendente), dobra a
   * edição no create para não enviar um id offline_ ao Postgres.
   */
  const updateServiceOrderOffline = useCallback(
    (data: {
      serviceOrderId: string;
      updates: Record<string, any>;
      items?: any[];
      photos?: any[];
      userId: string;
    }): void => {
      const folded = String(data.serviceOrderId).startsWith('offline_')
        ? handleOfflineMutationOfPendingRecord(data.serviceOrderId, {
            updates: data.updates,
            items: data.items,
            generalPhotos: data.photos,
          })
        : { removed: false };

      if (!folded.removed) {
        addToOfflineQueue('update_service_order', data);
      }

      const cached = getCachedServiceOrders(data.userId) || [];
      const idx = cached.findIndex((o: any) => o.id === data.serviceOrderId);
      if (idx !== -1) {
        cached[idx] = {
          ...cached[idx],
          ...data.updates,
          ...(data.items ? { _offline_items: data.items } : {}),
          ...(data.photos ? { _offline_photos: data.photos } : {}),
          _offline_pending: true,
        };
        cacheServiceOrders(data.userId, cached);
      }

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('service-order-updated'));
    },
    [],
  );

  /**
   * Enfileira a exclusão de uma OS. Se ainda é offline, apenas remove o create
   * pendente da fila.
   */
  const deleteServiceOrderOffline = useCallback(
    (data: {
      serviceOrderId: string;
      userId: string;
      /** Empresa dona dos movimentos — o executor precisa dela para devolver. */
      companyId?: string | null;
      /**
       * A DECISÃO de devolver estoque, tomada com o documento ainda vivo. O
       * cálculo fica para o sync: sem rede não dá para ler os movimentos, e
       * depois do delete o vínculo já virou NULL (ver stockDocumentReturn.ts).
       */
      returnStock?: boolean;
    }): void => {
      const handled = String(data.serviceOrderId).startsWith('offline_')
        ? handleOfflineMutationOfPendingRecord(data.serviceOrderId)
        : { removed: false };

      if (!handled.removed) {
        addToOfflineQueue('delete_service_order', data);
      }

      const cached = getCachedServiceOrders(data.userId) || [];
      cacheServiceOrders(data.userId, cached.filter((o: any) => o.id !== data.serviceOrderId));

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('service-order-updated'));
    },
    [],
  );

  /**
   * Enfileira a criação de um compromisso (agenda) e adiciona ao cache local
   * para exibição imediata. Compromisso é plano (sem filhos), como o lançamento
   * financeiro. Retorna o id temporário (offline_...).
   */
  const saveAppointmentOffline = useCallback(
    (
      data: { appointment: Record<string, any>; userId: string; companyId: string | null },
      options?: OfflineSaveOptions,
    ): string => {
      const operationId = addToOfflineQueue('create_appointment', data);

      const cached = getCachedAppointments(data.userId) || [];
      const now = new Date().toISOString();
      const tempAppointment = {
        id: operationId,
        ...data.appointment,
        user_id: data.userId,
        company_id: data.companyId,
        created_at: now,
        updated_at: now,
        _offline_pending: true,
      };
      cached.unshift(tempAppointment);
      cacheAppointments(data.userId, cached);

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('appointments-updated'));

      if (!options?.silent) {
        toast.success('Compromisso salvo localmente! Será sincronizado quando houver internet.', {
          icon: '📴',
        });
      }

      return operationId;
    },
    [],
  );

  /**
   * Enfileira a edição de um compromisso. Se ainda é offline (create pendente),
   * dobra a edição no create para não enviar um id offline_ ao Postgres.
   */
  const updateAppointmentOffline = useCallback(
    (data: { appointmentId: string; updates: Record<string, any>; userId: string }): void => {
      const folded = String(data.appointmentId).startsWith('offline_')
        ? handleOfflineMutationOfPendingRecord(data.appointmentId, { updates: data.updates })
        : { removed: false };

      if (!folded.removed) {
        addToOfflineQueue('update_appointment', data);
      }

      const cached = getCachedAppointments(data.userId) || [];
      const idx = cached.findIndex((a: any) => a.id === data.appointmentId);
      if (idx !== -1) {
        cached[idx] = { ...cached[idx], ...data.updates, _offline_pending: true };
        cacheAppointments(data.userId, cached);
      }

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('appointments-updated'));
    },
    [],
  );

  /**
   * Enfileira a exclusão de um compromisso. Se ainda é offline, apenas remove o
   * create pendente da fila.
   */
  const deleteAppointmentOffline = useCallback(
    (data: { appointmentId: string; userId: string }): void => {
      const handled = String(data.appointmentId).startsWith('offline_')
        ? handleOfflineMutationOfPendingRecord(data.appointmentId)
        : { removed: false };

      if (!handled.removed) {
        addToOfflineQueue('delete_appointment', data);
      }

      const cached = getCachedAppointments(data.userId) || [];
      cacheAppointments(data.userId, cached.filter((a: any) => a.id !== data.appointmentId));

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('appointments-updated'));
    },
    [],
  );

  /**
   * Enfileira a criação de um contrato (RASCUNHO) e adiciona ao cache local para
   * exibição imediata. Contrato é plano (sem filhos), como o compromisso. Só o
   * rascunho é offline: enviar para assinatura e assinar exigem internet.
   * Retorna o id temporário (offline_...).
   */
  const saveContractOffline = useCallback(
    (
      data: { contract: Record<string, any>; userId: string; companyId: string | null },
      options?: OfflineSaveOptions,
    ): string => {
      const operationId = addToOfflineQueue('create_contract', data);

      const cached = getCachedContracts(data.userId) || [];
      const now = new Date().toISOString();
      const tempContract = {
        id: operationId,
        ...data.contract,
        user_id: data.userId,
        company_id: data.companyId,
        status: data.contract.status || 'draft',
        created_at: now,
        updated_at: now,
        _offline_pending: true,
      };
      cached.unshift(tempContract);

      // Retry with heavy signature data URLs stripped if the write is rejected
      // (quota). The full data stays in the queue op and syncs normally.
      if (!cacheContracts(data.userId, cached)) {
        cacheContracts(data.userId, cached.map((c: any) => stripCachedContractMedia(c)));
      }

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('contracts-updated'));

      if (!options?.silent) {
        toast.success('Contrato salvo localmente! Será sincronizado quando houver internet.', {
          icon: '📴',
        });
      }

      return operationId;
    },
    [],
  );

  /**
   * Enfileira a edição de um contrato (RASCUNHO). Se ainda é offline (create
   * pendente), dobra a edição no create para não enviar um id offline_ ao
   * Postgres.
   */
  const updateContractOffline = useCallback(
    (data: { contractId: string; updates: Record<string, any>; userId: string }): void => {
      const folded = String(data.contractId).startsWith('offline_')
        ? handleOfflineMutationOfPendingRecord(data.contractId, { updates: data.updates })
        : { removed: false };

      if (!folded.removed) {
        addToOfflineQueue('update_contract', data);
      }

      const cached = getCachedContracts(data.userId) || [];
      const idx = cached.findIndex((c: any) => c.id === data.contractId);
      if (idx !== -1) {
        cached[idx] = { ...cached[idx], ...data.updates, _offline_pending: true };
        if (!cacheContracts(data.userId, cached)) {
          cacheContracts(data.userId, cached.map((c: any) => stripCachedContractMedia(c)));
        }
      }

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('contracts-updated'));
    },
    [],
  );

  /**
   * Enfileira a exclusão de um contrato. Se ainda é offline, apenas remove o
   * create pendente da fila.
   */
  const deleteContractOffline = useCallback(
    (data: { contractId: string; userId: string }): void => {
      const handled = String(data.contractId).startsWith('offline_')
        ? handleOfflineMutationOfPendingRecord(data.contractId)
        : { removed: false };

      if (!handled.removed) {
        addToOfflineQueue('delete_contract', data);
      }

      const cached = getCachedContracts(data.userId) || [];
      cacheContracts(data.userId, cached.filter((c: any) => c.id !== data.contractId));

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('contracts-updated'));
    },
    [],
  );

  /**
   * Enfileira a criação de um recibo e adiciona ao cache local para exibição
   * imediata. Recibo é plano (sem filhos), como o compromisso/contrato.
   * Retorna o id temporário (offline_...).
   */
  const saveReceiptOffline = useCallback(
    (
      data: { receipt: Record<string, any>; userId: string; companyId: string | null },
      options?: OfflineSaveOptions,
    ): string => {
      const operationId = addToOfflineQueue('create_receipt', data);

      const cached = getCachedReceipts(data.userId) || [];
      const now = new Date().toISOString();
      const tempReceipt = {
        id: operationId,
        ...data.receipt,
        user_id: data.userId,
        company_id: data.companyId,
        created_at: now,
        updated_at: now,
        _offline_pending: true,
      };
      cached.unshift(tempReceipt);

      // Retry with the heavy issuer-signature data URL stripped if the write is
      // rejected (quota). The full data stays in the queue op and syncs normally.
      if (!cacheReceipts(data.userId, cached)) {
        cacheReceipts(data.userId, cached.map((r: any) => stripCachedReceiptMedia(r)));
      }

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('receipts-updated'));

      if (!options?.silent) {
        toast.success('Recibo salvo localmente! Será sincronizado quando houver internet.', {
          icon: '📴',
        });
      }

      return operationId;
    },
    [],
  );

  /**
   * Enfileira a edição de um recibo. Se ainda é offline (create pendente), dobra
   * a edição no create para não enviar um id offline_ ao Postgres.
   */
  const updateReceiptOffline = useCallback(
    (data: { receiptId: string; updates: Record<string, any>; userId: string }): void => {
      const folded = String(data.receiptId).startsWith('offline_')
        ? handleOfflineMutationOfPendingRecord(data.receiptId, { updates: data.updates })
        : { removed: false };

      if (!folded.removed) {
        addToOfflineQueue('update_receipt', data);
      }

      const cached = getCachedReceipts(data.userId) || [];
      const idx = cached.findIndex((r: any) => r.id === data.receiptId);
      if (idx !== -1) {
        cached[idx] = { ...cached[idx], ...data.updates, _offline_pending: true };
        if (!cacheReceipts(data.userId, cached)) {
          cacheReceipts(data.userId, cached.map((r: any) => stripCachedReceiptMedia(r)));
        }
      }

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('receipts-updated'));
    },
    [],
  );

  /**
   * Enfileira a exclusão de um recibo. Se ainda é offline, apenas remove o create
   * pendente da fila.
   */
  const deleteReceiptOffline = useCallback(
    (data: { receiptId: string; userId: string }): void => {
      const handled = String(data.receiptId).startsWith('offline_')
        ? handleOfflineMutationOfPendingRecord(data.receiptId)
        : { removed: false };

      if (!handled.removed) {
        addToOfflineQueue('delete_receipt', data);
      }

      const cached = getCachedReceipts(data.userId) || [];
      cacheReceipts(data.userId, cached.filter((r: any) => r.id !== data.receiptId));

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('receipts-updated'));
    },
    [],
  );

  /**
   * Enfileira um MOVIMENTO de estoque (delta+motivo+refs — nunca o saldo
   * absoluto) e atualiza o cache de forma OTIMISTA: soma o delta ao saldo do
   * produto no cache do catálogo e grava um movimento provisório no histórico.
   * No sync, o worker chama a RPC apply_stock_movement, que RECOMPUTA a partir
   * do saldo real do servidor (o cliente não é autoritativo). Retorna o id
   * temporário (offline_...).
   */
  const saveStockMovementOffline = useCallback(
    (data: { movement: Record<string, any>; userId: string; companyId: string | null }): string => {
      const operationId = addToOfflineQueue('create_stock_movement', data);

      const delta = Number(data.movement?.delta ?? 0);
      const productId = data.movement?.product_id;
      let optimisticBalance: number | null = null;

      // Saldo otimista: soma o delta ao produto no cache do catálogo (badge/lista).
      if (productId) {
        const cachedCatalog = getCachedCatalog(data.userId) || [];
        const idx = cachedCatalog.findIndex((p: any) => p.id === productId);
        if (idx !== -1) {
          optimisticBalance = Number(cachedCatalog[idx].stock_quantity ?? 0) + delta;
          cachedCatalog[idx] = { ...cachedCatalog[idx], stock_quantity: optimisticBalance };
          cacheCatalog(data.userId, cachedCatalog);
        }
      }

      // Movimento provisório no histórico (substituído pelas linhas reais no sync).
      const cachedMovements = getCachedStockMovements(data.userId) || [];
      cachedMovements.unshift({
        id: operationId,
        ...data.movement,
        balance_after: optimisticBalance,
        user_id: data.userId,
        company_id: data.companyId,
        created_at: new Date().toISOString(),
        _offline_pending: true,
      });
      cacheStockMovements(data.userId, cachedMovements);

      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('catalog-updated'));
      window.dispatchEvent(new CustomEvent('stock-updated'));

      return operationId;
    },
    [],
  );

  /**
   * Verifica se está online de forma assíncrona (mais confiável)
   */
  const checkOnline = useCallback(async (): Promise<boolean> => {
    return await checkNetworkStatus();
  }, []);

  return {
    isOnline,
    checkOnline,
    saveQuoteOffline,
    saveClientOffline,
    saveCatalogItemOffline,
    saveFinanceEntryOffline,
    updateFinanceEntryOffline,
    deleteFinanceEntryOffline,
    saveServiceOrderOffline,
    updateServiceOrderOffline,
    deleteServiceOrderOffline,
    saveAppointmentOffline,
    updateAppointmentOffline,
    deleteAppointmentOffline,
    saveContractOffline,
    updateContractOffline,
    deleteContractOffline,
    saveReceiptOffline,
    updateReceiptOffline,
    deleteReceiptOffline,
    saveStockMovementOffline,
  };
}
